import type { SubEntryType } from "@tandemdiabetes/sugarmate-api-react-client"
import {
  Slider,
  SliderInput,
  SliderTracks,
  SliderValue,
  SliderValueInput,
  SliderValueLabel,
  SliderValueLabelContainer,
} from "@tandemdiabetes/sugarmate-react-components"
import { useCallback } from "react"
import { usePolyglot } from "src/contexts"
import { useSliderInputProps } from "src/hooks"
import styles from "src/styles/entryModal.module.scss"
import type { EditableSubEntry } from "src/types"

interface Props<T extends SubEntryType> {
  currentSubEntry: EditableSubEntry<T>;
  updateSubEntry: (s: Partial<EditableSubEntry<T>>) => void;
  min?: number;
  max?: number;
  sliderStep?: number;
  inputStep?: number;
  readOnly?: boolean;
}

const id = "subentry-duration-input"

export function DurationInput<T extends SubEntryType>(props: Props<T>) {
  const {
    currentSubEntry,
    updateSubEntry,
    min = 0,
    max = 300,
    sliderStep = 5,
    inputStep = 1,
    readOnly = false,
  } = props

  const polyglot = usePolyglot()

  const onChange = useCallback(
    (durationInMinutes: number | null) => {
      updateSubEntry({
        duration_in_seconds: typeof durationInMinutes === "number"
          ? durationInMinutes * 60
          : null,
      })
    },
    [updateSubEntry],
  )

  const value = typeof currentSubEntry.duration_in_seconds === "number"
    ? currentSubEntry.duration_in_seconds / 60
    : null

  const {
    inputValue,
    sliderValue,
    onNumberChange,
    onSliderChange,
    onNumberBlur,
    onNumberKeyDown,
  } = useSliderInputProps({
    min,
    max,
    inputStep,
    sliderStep,
    value,
    onChange,
    allowEmptyValue: true,
    emptyValue: null,
  })

  return (
    <div
      className={styles.row}
      id="device-duration"
    >
      <SliderValue sizePreset="large">
        <SliderValueInput
          max={max}
          min={min}
          step={inputStep}
          id={id}
          value={inputValue}
          onChange={onNumberChange}
          onBlur={onNumberBlur}
          onKeyDown={onNumberKeyDown}
          readOnly={readOnly}
        />
        <SliderValueLabelContainer>
          <SliderValueLabel htmlFor={id}>
            {polyglot.t(
              "components.entry_modal.duration.label_detail",
              value ?? min,
            )}
          </SliderValueLabel>
        </SliderValueLabelContainer>
      </SliderValue>
      {!readOnly && (
        <Slider
          className={styles.rangeInputSlider}
          id="duration-input-slider"
        >
          <SliderInput
            min={min}
            max={max}
            step={sliderStep}
            id={id}
            value={sliderValue ?? min}
            onChange={onSliderChange}
          />
          <SliderTracks
            min={min}
            max={max}
          />
        </Slider>
      )}
    </div>
  )
}
